import { useState, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { useDebugRerenders } from '@wix/da-hooks/pkg/useDebugRerenders';
import { convertFromPopperPlacement, convertToPopperPlacement, } from '../helpers';
import sassVariables from '../../../styles/variables.scss';
var wrapperMaxWidth = parseInt(sassVariables['wrapper_max_width']);
/** This is our base popper wrapper that applies our custom modifiers etc */
export function useBasePopper(_a) {
    var disablePlacementModifier = _a.disablePlacementModifier, placement = _a.placement, secondaryPlacement = _a.secondaryPlacement, referenceElement = _a.referenceElement, positionFixed = _a.positionFixed, _b = _a.eventsEnabled, eventsEnabled = _b === void 0 ? true : _b, offsetModifierProp = _a.offsetModifier, _c = _a.allowFlip, allowFlip = _c === void 0 ? true : _c;
    var _d = useState(null), popperElement = _d[0], setPopperElement = _d[1];
    var _e = useState(null), arrowElement = _e[0], setArrowElement = _e[1];
    var memoizedOffsetModifierProp = useDeepEqualMemo(offsetModifierProp);
    /* This modifier will force the popover to not be centered when it reaches
      the max width, as per designers request. By default it sets the `start`
      placement variant when that is the case, but if the popover doesn't fit
      the built-in `flip` modifier will kick in and position it accordingly. */
    var overridePlacement = useMemo(function () { return ({
        name: 'overridePlacement',
        enabled: !disablePlacementModifier,
        phase: 'beforeMain',
        requiresIfExists: ['offsets', 'flip'],
        fn: function (_a) {
            var state = _a.state;
            var width = state.rects.popper.width;
            var maxWidth = disablePlacementModifier
                ? Number.POSITIVE_INFINITY
                : wrapperMaxWidth;
            // eslint-disable-next-line @typescript-eslint/no-shadow
            var _b = convertFromPopperPlacement(state.placement), placement = _b.placement, secondaryPlacement = _b.secondaryPlacement;
            if (width >= maxWidth && !secondaryPlacement) {
                state.placement = convertToPopperPlacement(placement, 'start');
                state.reset = true;
            }
        },
    }); }, [disablePlacementModifier]);
    var arrowModifier = useMemo(function () { return ({
        name: 'arrow',
        enabled: !!arrowElement,
        options: { element: arrowElement },
    }); }, [arrowElement]);
    var eventListenerModifier = useMemo(function () { return ({
        name: 'eventListeners',
        enabled: eventsEnabled,
        options: {
            scroll: true,
            resize: true,
        },
    }); }, [eventsEnabled]);
    var offsetModifier = useMemo(function () { return ({
        name: 'offset',
        options: { offset: memoizedOffsetModifierProp },
    }); }, [memoizedOffsetModifierProp]);
    var flipModifier = useMemo(function () { return ({
        name: 'flip',
        enabled: allowFlip,
    }); }, [allowFlip]);
    var memoizedModifiers = useDeepEqualMemo([
        overridePlacement,
        arrowModifier,
        eventListenerModifier,
        offsetModifier,
        flipModifier,
    ]);
    var _f = usePopper(referenceElement, popperElement, {
        placement: convertToPopperPlacement(placement, secondaryPlacement),
        strategy: positionFixed ? 'fixed' : 'absolute',
        modifiers: memoizedModifiers,
    }), styles = _f.styles, attributes = _f.attributes, update = _f.update, state = _f.state;
    useDebugRerenders('useBasePopper', {
        disablePlacementModifier: disablePlacementModifier,
        placement: placement,
        secondaryPlacement: secondaryPlacement,
        referenceElement: referenceElement,
        positionFixed: positionFixed,
        eventsEnabled: eventsEnabled,
        offsetModifier: offsetModifier,
        allowFlip: allowFlip,
        popperElement: popperElement,
        arrowElement: arrowElement,
    });
    var _g = convertFromPopperPlacement(state === null || state === void 0 ? void 0 : state.placement), effectivePlacement = _g.placement, effectiveSecondaryPlacement = _g.secondaryPlacement;
    return {
        rootRef: setPopperElement,
        arrowRef: setArrowElement,
        contentStyle: styles.popper,
        arrowStyle: styles.arrow,
        contentAttributes: attributes.popper,
        arrowAttributes: attributes.arrow,
        effectivePlacement: effectivePlacement,
        effectiveSecondaryPlacement: effectiveSecondaryPlacement,
        updatePositioning: update !== null && update !== void 0 ? update : undefined,
    };
}
//# sourceMappingURL=useBasePopper.js.map